import React, { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { TipType, useGetUpcomingTips } from "@api";

import { InfoBox, TitleBlock } from "@ui/components";
import { Button, Text } from "@ui/elements";
import { ButtonSelect } from "@ui/forms";
import { Stack } from "@ui/layout";

import { TipBox, TipCardSkeleton, TipsTable } from "@components";

import "./UpcomingTipsPanel.scss";

export const UpcomingTipsPanel = () => {
  const [type, setType] = useState<TipType>("resultOriented" as const);
  const { t } = useTranslation();
  const { data: upcomingTipsData, isLoading: upcomingTipsLoading } =
    useGetUpcomingTips({
      from: dayjs().format("YYYY-MM-DD"),
      pageSize: 3,
      type,
    });

  const showMoreText = useMemo(() => {
    if (upcomingTipsData?.data && upcomingTipsData.totalItems > 3) {
      return (
        <Text>
          <Trans
            i18nKey="view-more-matches"
            values={{ count: upcomingTipsData?.totalItems - 3 }}
          />
        </Text>
      );
    }

    return t("labels.show-all");
  }, [upcomingTipsData]);

  return (
    <Stack direction="column" gap="md" align="start" isFullwidth>
      <TitleBlock
        title={t("navigation.upcoming-tips")}
        subtitle={t("upcoming-tips-subtitle")}
      >
        <ButtonSelect
          isGrouped={false}
          options={[
            {
              icon: "Target",
              label: t("tip-type.result-oriented"),
              value: "resultOriented",
            },
            {
              icon: "ChartBar",
              label: t("tip-type.value-based-short"),
              value: "value",
            },
          ]}
          value={type}
          onChange={(t) => setType(t as TipType)}
        />
      </TitleBlock>
      <Stack
        direction="column"
        gap="xl"
        className="upcoming-tips-panel-wrapper"
        isFullwidth
      >
        {!upcomingTipsLoading && !upcomingTipsData?.data.length && (
          <InfoBox
            icon="SmileyXEyes"
            hasSpacing
            title={t("messages.no-tips-found-title")}
          >
            <Trans i18nKey="messages.no-tips-found" />
          </InfoBox>
        )}
        {upcomingTipsLoading && (
          <>
            <TipCardSkeleton />
            <TipCardSkeleton />
          </>
        )}
        {upcomingTipsData?.data.map(({ fixture, tips }) => (
          <TipBox
            key={`upcoming-tip-dashboard-card-${fixture.fixture.id}`}
            fixture={fixture}
            hasTips={!!tips.length}
          >
            <TipsTable
              tips={tips}
              fixture={fixture}
              type={type}
              allowSaveEdit
              allowCurrency
            />
          </TipBox>
        ))}
      </Stack>
      <Link to={`/upcoming-tips?activeTab=${type}`}>
        <Button variant="text">{showMoreText}</Button>
      </Link>
    </Stack>
  );
};
